/**
 * Ticket LM1: Lead-Magnet-Mechanik – gemeinsame Typen, Prüfregeln und reine Logik.
 *
 * Bewusst ohne React und ohne Datenbankzugriff, damit Fristen, Adressen und
 * Kollisionsprüfung testbar bleiben.
 *
 * Alle öffentlichen Adressen liegen unter `/kostenlos/`. Ein Kürzel auf
 * oberster Ebene würde früher oder später mit einer bestehenden oder künftigen
 * Seite kollidieren – und diese Adressen stehen dann schon in Anzeigen.
 */

import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
import { BUNDESLAND_LABELS } from "@/lib/contacts";
import type { ClientModuleKey } from "@/lib/client-modules";
import type { ToolAccessPlan } from "@/lib/lead-magnet-access";
import type { PublicPerson } from "@/lib/team-people";
import { normalizeTtpaSlug } from "@/lib/ttpa-slug";

export type LeadMagnetKind = Database["public"]["Enums"]["lead_magnet_kind"];
export type LeadMagnetStatus = Database["public"]["Enums"]["lead_magnet_status"];

export const LEAD_MAGNET_KIND_LABELS: Record<LeadMagnetKind, string> = {
  datei: "Datei zum Download",
  werkzeug: "Werkzeug mit Zugang im Cockpit",
};

export const LEAD_MAGNET_STATUS_LABELS: Record<LeadMagnetStatus, string> = {
  entwurf: "Entwurf",
  veroeffentlicht: "veröffentlicht",
};

/** Gemeinsames Präfix aller Freebie-Adressen. */
export const LEAD_MAGNET_PREFIX = "/kostenlos";

/**
 * Feste Unterseiten unter `/kostenlos/`. Diese Kürzel darf kein Lead-Magnet
 * belegen, sonst wäre die Bestätigungsstrecke nicht mehr erreichbar. Der Test
 * `tests/lib/lead-magnet-collisions.test.ts` schlägt fehl, sobald eine neue
 * Route unter `/kostenlos/` auftaucht, ohne hier gesperrt zu sein.
 */
export const RESERVED_LEAD_MAGNET_SLUGS: string[] = ["bestaetigen"];

export function isReservedLeadMagnetSlug(slug: string): boolean {
  return RESERVED_LEAD_MAGNET_SLUGS.includes(slug.trim().toLowerCase());
}

/**
 * Ticket LM6: Feste Adresse der Dankeseite. Sie liegt immer unterhalb der
 * Landingpage; niemand tippt sie ein.
 */
export const DEFAULT_THANKS_SLUG = "danke";

/**
 * Ticket LM6: Das Kürzel entsteht aus dem Titel – normalisiert nach dem
 * Verfahren aus Ticket T3a (Kleinschreibung, Umlaute und ß umgeschrieben,
 * Sonder- und Leerzeichen zu einzelnen Bindestrichen, keine Bindestriche am
 * Rand). Zu kurze Ergebnisse geben "" zurück, damit das Formular still bleibt,
 * solange der Titel erst begonnen wurde.
 */
export function deriveLeadMagnetSlug(title: string): string {
  const slug = normalizeTtpaSlug(title).slice(0, 80).replace(/-+$/g, "");
  return slug.length >= 3 ? slug : "";
}

/**
 * Ticket LM6: Kollisions- und Formprüfung fürs Formular, damit der Hinweis
 * sofort erscheint und nicht erst beim Speichern.
 */
export function leadMagnetSlugIssue(slug: string, takenSlugs: string[]): string | null {
  const value = slug.trim().toLowerCase();
  if (!value) return "Bitte ein Kürzel angeben.";
  const parsed = slugSchema.safeParse(value);
  if (!parsed.success) return parsed.error.issues[0]?.message ?? "Ungültiges Kürzel.";
  if (isReservedLeadMagnetSlug(value)) {
    return `„${value}“ ist unter /kostenlos/ für eine feste Seite vergeben.`;
  }
  if (takenSlugs.map((s) => s.trim().toLowerCase()).includes(value)) {
    return `„${value}“ ist bereits von einem anderen Freebie belegt.`;
  }
  return null;
}

/**
 * Ticket LM11: Ist das Kürzel gesperrt? Ab der ersten Veröffentlichung ja –
 * es sei denn, jemand hat die Sperre im Formular bewusst aufgehoben.
 * Als eigene Funktion, damit die Sperre am Verhalten geprüft werden kann.
 */
export function isLeadMagnetSlugLocked(
  firstPublishedAt: string | null | undefined,
  unlocked = false,
): boolean {
  return Boolean(firstPublishedAt) && !unlocked;
}

/**
 * Ticket LM6: Die Fassung des Wortlauts wird am Freebie ausschließlich als Zahl
 * geführt. Die Anmeldungen halten ihre Fassung als eigene Kopie fest – in der
 * Schreibweise, die sie seit Ticket LM1 erwarten.
 */
export function formatConsentVersion(versionNo: number): string {
  return `v${Math.max(1, Math.trunc(versionNo))}`;
}

/** Lesbare Anzeige der geführten Fassung im internen Bereich. */
export function describeConsentVersion(
  versionNo: number,
  changedAt: string | null,
): string {
  const base = `Fassung ${Math.max(1, Math.trunc(versionNo))}`;
  if (!changedAt) return `${base}, bisher unverändert`;
  const date = new Date(changedAt);
  if (Number.isNaN(date.getTime())) return base;
  return `${base}, zuletzt geändert am ${date.toLocaleDateString("de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })}`;
}

/** Fristen an einem Ort, damit Mailtexte und Prüfungen nicht auseinanderlaufen. */
export const CONFIRM_TTL_HOURS = 72;
export const DELIVERY_TTL_DAYS = 7;
export const DOWNLOAD_TTL_SECONDS = 15 * 60;

const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, "Mindestens 3 Zeichen")
  .max(80, "Höchstens 80 Zeichen")
  .regex(SLUG_RE, "Nur Kleinbuchstaben, Zahlen und Bindestriche");

export type LeadMagnetQuestionType = "text" | "auswahl";

export const leadMagnetQuestionSchema = z.object({
  id: z.string().trim().min(1).max(40),
  label: z.string().trim().min(1, "Bitte Frage angeben").max(200),
  type: z.enum(["text", "auswahl"]),
  options: z.array(z.string().trim().min(1).max(120)).max(12).default([]),
  required: z.boolean().default(false),
});

export type LeadMagnetQuestion = z.infer<typeof leadMagnetQuestionSchema>;

/** Höchstens zwei Fragen: mehr senkt die Abschlussquote spürbar. */
export const LEAD_MAGNET_MAX_QUESTIONS = 2;

export const leadMagnetFormSchema = z
  .object({
    slug: slugSchema,
    title: z.string().trim().min(1, "Bitte Titel angeben").max(200),
    subtitle: z.string().trim().max(300).default(""),
    /**
     * Ticket LM8: Kurzfassung für die Übersichtsseite. Bewusst hart begrenzt –
     * die Liste lebt davon, dass jeder Eintrag gleich schnell zu lesen ist.
     */
    shortDescription: z
      .string()
      .trim()
      .max(200, "Die Kurzbeschreibung darf höchstens 200 Zeichen haben.")
      .default(""),
    /** Ticket LM8: Fließtext unterhalb des Formularbereichs der Landingpage. */
    bodyMd: z.string().trim().max(20000).default(""),
    /** Ticket LM8: eigene Angaben für Suchmaschinen; leer = Ableitung wie bisher. */
    seoTitle: z.string().trim().max(70, "Der SEO-Titel darf höchstens 70 Zeichen haben.").default(""),
    seoDescription: z
      .string()
      .trim()
      .max(160, "Die SEO-Beschreibung darf höchstens 160 Zeichen haben.")
      .default(""),

    kind: z.enum(["datei", "werkzeug"]),
    questions: z.array(leadMagnetQuestionSchema).max(LEAD_MAGNET_MAX_QUESTIONS).default([]),
    consentText: z.string().trim().max(2000).default(""),
    confirmationText: z.string().trim().max(2000).default(""),
    status: z.enum(["entwurf", "veroeffentlicht"]),
    filePath: z.string().trim().max(500).nullable().default(null),
    /** Ticket WS1: Vorschaubild für die Übersichtsseite (eigener Ablagebereich). */
    previewImagePath: z.string().trim().max(500).nullable().default(null),
    fileName: z.string().trim().max(300).nullable().default(null),
    fileSize: z.number().int().nonnegative().nullable().default(null),
    /** Ticket LM3: freizuschaltendes Werkzeug (nur bei kind = werkzeug). */
    accessModuleKey: z.enum(["hundert_tage", "wahlkalender"]).nullable().default(null),
  })
  .superRefine((value, ctx) => {
    if (isReservedLeadMagnetSlug(value.slug)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["slug"],
        message: `„${value.slug}“ ist unter /kostenlos/ für eine feste Seite vergeben.`,
      });
    }

    // Ticket LM3: Ein Werkzeug-Freebie ohne hinterlegtes Werkzeug hätte nach der
    // Bestätigung nichts freizuschalten – deshalb Pflichtangabe.
    if (value.kind === "werkzeug" && !value.accessModuleKey) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["accessModuleKey"],
        message: "Bitte das freizuschaltende Werkzeug auswählen.",
      });
    }
    if (value.status === "veroeffentlicht" && value.kind === "datei" && !value.filePath) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["status"],
        message: "Ohne hinterlegte Datei ist keine Veröffentlichung möglich.",
      });
    }
    for (const [index, question] of value.questions.entries()) {
      if (question.type === "auswahl" && question.options.length < 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["questions", index, "options"],
          message: "Eine Auswahlfrage braucht mindestens zwei Antworten.",
        });
      }
    }
  });

export type LeadMagnetFormValues = z.infer<typeof leadMagnetFormSchema>;

/**
 * Ticket LM14: Plausibilitätsprüfung der Anmeldedaten. Bot-Schutz und
 * Bestätigungsmail halten Maschinen fern, nicht aber Menschen mit Fantasienamen
 * und Wegwerfadressen. Diese Prüfung sperrt das Offensichtliche – im Formular
 * für einen klaren Hinweis und im Schema der Serverfunktion als eigentliche
 * Sperre. Bewusst keine Markierung: was hier hängen bleibt, wird kein Kontakt.
 */
export const LEAD_MAGNET_NAME_MIN_LETTERS = 3;

const NAME_CHARS_RE = /^[\p{L}\p{M}'’ .-]+$/u;
/** Punkt direkt vor einem Buchstaben: Adresse statt Name („www.spam.de“). */
const NAME_DOMAIN_RE = /\.\p{L}/u;

export function leadMagnetNameIssue(value: string, field: "Vorname" | "Nachname"): string | null {
  const name = value.trim();
  const letters = name.match(/\p{L}/gu)?.length ?? 0;
  if (
    !NAME_CHARS_RE.test(name) ||
    NAME_DOMAIN_RE.test(name) ||
    letters < LEAD_MAGNET_NAME_MIN_LETTERS
  ) {
    return `Bitte geben Sie Ihren vollständigen ${field}n an (mindestens ${LEAD_MAGNET_NAME_MIN_LETTERS} Buchstaben).`;
  }
  return null;
}

/** Teilwörter, die im vorderen Teil einer echten Adresse nicht vorkommen. */
const SUSPICIOUS_LOCAL_FRAGMENTS = [
  "spam",
  "fake",
  "trash",
  "junk",
  "wegwerf",
  "asdf",
  "qwert",
  "dummy",
  "noreply",
  "no-reply",
  "donotreply",
];

/**
 * Nur als ganzes Wort gesperrt (getrennt durch Punkt, Ziffern o. Ä.), weil sie
 * als Teilwort auch in echten Namen stecken („Testorf“, „Mueller“).
 */
const SUSPICIOUS_LOCAL_WORDS = [
  "test",
  "tester",
  "testing",
  "testmail",
  "testuser",
  "muell",
  "müll",
  "xxx",
  "nomail",
  "foo",
];

/**
 * Bekannte Wegwerf-Anbieter (auch Subdomains). Reservierte Domains wie
 * example.com fehlen bewusst: sie empfangen keine Post, die Bestätigung
 * scheitert dort ohnehin.
 */
const BLOCKED_EMAIL_DOMAINS = [
  "mailinator.com",
  "yopmail.com",
  "trashmail.com",
  "trashmail.de",
  "trash-mail.com",
  "wegwerfmail.de",
  "wegwerfmail.net",
  "muellmail.com",
  "spoofmail.de",
  "sofort-mail.de",
  "byom.de",
  "einrot.com",
  "10minutemail.com",
  "guerrillamail.com",
  "sharklasers.com",
  "temp-mail.org",
  "tempmail.com",
  "tempail.com",
  "throwawaymail.com",
  "dispostable.com",
  "discard.email",
  "maildrop.cc",
  "getnada.com",
  "fakeinbox.com",
  "mailnesia.com",
  "spamgourmet.com",
];

export function leadMagnetEmailIssue(value: string): string | null {
  const email = value.trim().toLowerCase();
  const at = email.lastIndexOf("@");
  if (at <= 0) return null;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  const words = local.split(/[^a-zäöüß]+/).filter(Boolean);
  const suspicious =
    SUSPICIOUS_LOCAL_FRAGMENTS.some((fragment) => local.includes(fragment)) ||
    words.some((word) => SUSPICIOUS_LOCAL_WORDS.includes(word)) ||
    BLOCKED_EMAIL_DOMAINS.some((d) => domain === d || domain.endsWith(`.${d}`));
  return suspicious ? "Bitte verwenden Sie Ihre persönliche oder berufliche E-Mail-Adresse." : null;
}

function refineWith(check: (value: string) => string | null) {
  return (value: string, ctx: z.RefinementCtx) => {
    const message = check(value);
    if (message) ctx.addIssue({ code: z.ZodIssueCode.custom, message });
  };
}

export const leadMagnetSignupSchema = z.object({
  slug: slugSchema,
  firstName: z
    .string()
    .trim()
    .min(1, "Bitte Vornamen angeben")
    .max(100)
    .superRefine(refineWith((v) => leadMagnetNameIssue(v, "Vorname"))),
  lastName: z
    .string()
    .trim()
    .min(1, "Bitte Nachnamen angeben")
    .max(100)
    .superRefine(refineWith((v) => leadMagnetNameIssue(v, "Nachname"))),
  email: z
    .string()
    .trim()
    .email("Ungültige E-Mail-Adresse")
    .max(255)
    .superRefine(refineWith(leadMagnetEmailIssue)),
  answers: z
    .array(z.object({ id: z.string().max(40), label: z.string().max(200), value: z.string().max(500) }))
    .max(LEAD_MAGNET_MAX_QUESTIONS)
    .default([]),
  /**
   * Ticket DM1: Zwei getrennte Haken beim Werkzeug-Weg. `termsAccepted` ist
   * dort Pflicht (ohne Haken entsteht kein Konto), `marketingConsent` ist
   * freiwillig und leer vorbelegt. Beim Datei-Weg bleibt der bisherige
   * Einwilligungstext (`consentText` des Magneten) maßgeblich; beide Felder
   * sind dann ohne Belang.
   */
  termsAccepted: z.boolean().optional(),
  marketingConsent: z.boolean().optional(),
  /**
   * Ticket WK7: Seite, über die die Anmeldung erfolgte. Bleibt das Feld weg,
   * gilt die Freebie-Seite selbst – bisheriges Verhalten ohne Änderung. Die
   * Herkunftsangabe am Kontakt baut der Server daraus (siehe
   * `leadMagnetOriginLabel`); eine frei erfundene Bezeichnung nimmt die
   * Schnittstelle nicht an.
   */
  sourcePage: z.enum(["freebie", "landesseite"]).optional(),
  /** Nur bei sourcePage „landesseite": Bundesland-Schlüssel der Seite. */
  sourceBundesland: z.string().trim().max(60).optional(),
  turnstileToken: z.string().min(1),
});

/**
 * Ticket WK7: Herkunftsangabe einer Anmeldung im Wortlaut der bestehenden
 * Kennzeichnung (`contact_origins.lead_magnet`). Die Landesseite erhält ihre
 * eigene Zeile neben der der Freebie-Seite – unterscheidbar, ohne zweiten Weg.
 * Ein unbekanntes Bundesland fällt auf den reinen Titel zurück, statt eine
 * erfundene Bezeichnung zu schreiben.
 */
export type LeadMagnetSourcePage = "freebie" | "landesseite";

export function leadMagnetOriginLabel(
  magnetTitle: string,
  sourcePage?: LeadMagnetSourcePage,
  sourceBundesland?: string,
): string {
  if (sourcePage !== "landesseite") return magnetTitle;
  const label = sourceBundesland
    ? (BUNDESLAND_LABELS as Record<string, string>)[sourceBundesland]
    : undefined;
  return label ? `${magnetTitle} — Landesseite ${label}` : magnetTitle;
}

/**
 * Ticket DM1: Feste Wortlaut-Versionen der beiden Haken beim Werkzeug-Weg.
 * Ändert sich ein Wortlaut, wird die Version erhöht – so bleibt nachweisbar,
 * welchem Text eine Person zugestimmt hat.
 */
export const TOOL_TERMS_WORDING_VERSION = "nutzungsbedingungen-2026-09";
export const TOOL_MARKETING_WORDING_VERSION = "werbliche-ansprache-2026-09";

export type LeadMagnetSignupInput = z.infer<typeof leadMagnetSignupSchema>;

/** Öffentliche Ansicht eines Freebies (nur veröffentlichte Datensätze). */
export type PublicLeadMagnet = {
  slug: string;
  thanksSlug: string;
  title: string;
  subtitle: string;
  /** Ticket LM8: Fließtext unter dem Formularbereich (Markdown). */
  bodyMd: string;
  /** Ticket LM8: eigene Suchmaschinen-Angaben; leer = Ableitung aus Titel/Untertitel. */
  seoTitle: string;
  seoDescription: string;
  /** Ticket LM8: dasselbe Vorschaubild wie in der Übersicht, signiert. */
  previewImageUrl: string | null;

  kind: LeadMagnetKind;
  questions: LeadMagnetQuestion[];
  consentText: string;
  consentVersion: string;
  confirmationText: string;
  fileName: string | null;
  /** Ticket LM3: gesetzt bei kind = werkzeug. */
  accessModuleKey: ClientModuleKey | null;
  /** Ticket W11: Personenkarten des Angebots (nur öffentlich gekennzeichnete). */
  people: PublicPerson[];
};

/** Interne Ansicht mit Zählern. */
export type LeadMagnetRow = PublicLeadMagnet & {
  id: string;
  status: LeadMagnetStatus;
  filePath: string | null;
  fileSize: number | null;
  signupsTotal: number;
  signupsConfirmed: number;
  createdAt: string;
  updatedAt: string;
};

export type LeadMagnetSignupRow = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  answers: { id: string; label: string; value: string }[];
  confirmedAt: string | null;
  createdAt: string;
};

export function leadMagnetPath(slug: string): string {
  return `${LEAD_MAGNET_PREFIX}/${slug}`;
}

export function leadMagnetThanksPath(slug: string, thanksSlug: string): string {
  return `${LEAD_MAGNET_PREFIX}/${slug}/${thanksSlug}`;
}

export function leadMagnetConfirmPath(): string {
  return `${LEAD_MAGNET_PREFIX}/bestaetigen`;
}

/**
 * Ticket WS1: Öffentliche Übersicht aller kostenlosen Angebote. Die Adresse ist
 * stabil und wird in Anzeigen verwendet.
 */
export const LEAD_MAGNET_OVERVIEW_PATH = "/wissen/downloads";

/**
 * Ticket WS1: Eigener Ablagebereich für die öffentlich gezeigten Vorschaubilder.
 * Bewusst getrennt von `lead-magnet-files`: dort liegt ausschließlich, was erst
 * nach bestätigter Anmeldung ausgeliefert wird. Ein Fehler in einer Richtlinie
 * darf nie beide Zwecke gleichzeitig treffen.
 */
export const LEAD_MAGNET_PREVIEW_BUCKET = "lead-magnet-previews";

/** Ticket WS1: Ein Eintrag der Übersichtsseite. */
export type LeadMagnetOverviewItem = {
  slug: string;
  title: string;
  subtitle: string;
  /** Ticket LM8: Kurzfassung – die Übersicht zeigt bewusst nur diese. */
  shortDescription: string;

  kind: LeadMagnetKind;
  /** Kurzlebig signierte Adresse oder `null` (dann erscheint die Platzhalterfläche). */
  previewImageUrl: string | null;
};

/**
 * Ticket WS1: „herunterladen“ wäre bei einem Zugang schlicht falsch – deshalb
 * unterscheidet die Beschriftung zwischen Datei und Werkzeug.
 */
export function leadMagnetCtaLabel(kind: LeadMagnetKind): string {
  return kind === "werkzeug" ? "Kostenlos nutzen" : "Kostenlos herunterladen";
}

export function confirmExpiresAt(from: Date): Date {
  return new Date(from.getTime() + CONFIRM_TTL_HOURS * 60 * 60 * 1000);
}

export function deliveryExpiresAt(from: Date): Date {
  return new Date(from.getTime() + DELIVERY_TTL_DAYS * 24 * 60 * 60 * 1000);
}

export function isExpired(expiresAt: string | null, now: Date = new Date()): boolean {
  if (!expiresAt) return true;
  const ts = new Date(expiresAt).getTime();
  if (Number.isNaN(ts)) return true;
  return ts <= now.getTime();
}

export type ConfirmOutcome =
  | {
      status: "ok";
      slug: string;
      thanksSlug: string;
      /** Nur bei Datei-Freebies: Kennung für den Download. */
      deliveryToken: string | null;
      title: string;
      /** Ticket LM3: Ausgang der Kontoanlage bei Werkzeug-Freebies. */
      toolOutcome?: ToolAccessPlan["outcome"];
      /** Erste Bestätigung dieser Anmeldung: nur dann zählt das Freebie-Ereignis. */
      firstCompletion: boolean;
    }
  | { status: "expired"; slug: string }
  | { status: "unknown" };

export type DeliveryOutcome =
  | { status: "ok"; title: string; fileName: string; downloadUrl: string; expiresAt: string }
  | { status: "expired"; slug: string }
  | { status: "unknown" };

/** Antworten auf Qualifizierungsfragen prüfen (Pflichtfelder, erlaubte Auswahl). */
export function validateAnswers(
  questions: LeadMagnetQuestion[],
  answers: { id: string; value: string }[],
): { ok: true } | { ok: false; message: string } {
  for (const question of questions) {
    const value = answers.find((a) => a.id === question.id)?.value?.trim() ?? "";
    if (question.required && !value) {
      return { ok: false, message: `Bitte „${question.label}“ ausfüllen.` };
    }
    if (question.type === "auswahl" && value && !question.options.includes(value)) {
      return { ok: false, message: `Ungültige Auswahl bei „${question.label}“.` };
    }
  }
  return { ok: true };
}
